/**
 * * 扩展轴分割线功能，支持自定义分割线样式和 iOS 16 兼容性修复
 *
 * - 支持通过函数动态设置每条分割线的样式
 * - 修复 iOS 16 下分割线显示异常的问题
 * - 适用于 xAxis、yAxis、singleAxis
 *
 * @example
 * ```javascript
 * {
 *   yAxis: {
 *     splitLine: {
 *       show: true,
 *       // 函数式动态样式，每条分割线可以有不同样式
 *       lineStyle: function(index, label) {
 *         // index: 分割线索引 (0, 1, 2, ...)
 *         // label: 对应的轴标签值
 *         if (index % 2 === 0) {
 *           return {
 *             color: '#ff0000',    // 偶数索引用红色
 *             width: 2,
 *             type: 'solid'
 *           };
 *         } else {
 *           return {
 *             color: '#00ff00',    // 奇数索引用绿色
 *             width: 1,
 *             type: 'dashed'
 *           };
 *         }
 *       }
 *     }
 *   }
 * }
 * ```
 *
 * @example
 * ```javascript
 * // 基于标签值设置样式
 * {
 *   xAxis: {
 *     data: ['周一', '周二', '周三', '周四', '周五'],
 *     splitLine: {
 *       show: true,
 *       lineStyle: function(index, label) {
 *         // 周末用不同颜色
 *         if (label === '周五') {
 *           return {
 *             color: '#ff6b6b',
 *             width: 3,
 *             type: 'solid'
 *           };
 *         }
 *         return {
 *           color: '#e0e0e0',
 *           width: 1,
 *           type: 'solid'
 *         };
 *       }
 *     }
 *   }
 * }
 * ```
 */
import type GlobalModel from 'echarts/lib/model/Global';
import type ExtensionAPI from 'echarts/lib/core/ExtensionAPI';
import { type EChartsExtensionInstallRegisters } from 'echarts/lib/extension';
import type AxisModel from 'echarts/lib/coord/cartesian/AxisModel';
import { isFunction, merge } from 'zrender/lib/core/util.js';
import Group from 'zrender/lib/graphic/Group.js';
import Text from 'zrender/lib/graphic/Text.js';

// 已经适用的轴线类型
const usageAxisTypes = ['xAxis', 'yAxis', 'singleAxis'];

function splitLineExtend(registers: EChartsExtensionInstallRegisters) {
  registers.registerUpdateLifecycle('afterupdate', (ecModel: GlobalModel, api: ExtensionAPI) => {
    ecModel.eachComponent((componentType: string, compModel: AxisModel) => {
      if (!usageAxisTypes.includes(componentType) || !compModel?.get('show')) {
        return;
      }

      const { option } = compModel;
      const compView = api.getViewOfComponentModel(compModel);
      const lines = [];
      // @ts-ignore
      (compView.group as Group).traverse(childEl => {
        if ((childEl.anid || '').startsWith('line_')) {
          lines.push(childEl as Text);
        }
      });

      // 自定义分割线样式
      if (isFunction(option?.splitLine?.lineStyle)) {
        lines.forEach((line, index) => {
          const { anid = '' } = line;
          const label = anid.split('_')[1];
          const lineStyle = { ...line.style };
          const customLineStyle = option.splitLine.lineStyle(index, label);
          merge(lineStyle, customLineStyle, true);
          line.attr({ style: lineStyle });
        });
      }

      // IOS16分割线样式异常补丁
      lines.forEach(line => {
        const { lineWidth } = line.style;
        line.attr({ style: { lineWidth: lineWidth * (1 + 0.1 * (0.5 + 0.5 * Math.random())) } });
      });
    });
  });
}

export function install(registers: EChartsExtensionInstallRegisters) {
  splitLineExtend(registers);
}
